import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import AssistantAnswer from "./AssistantAnswer";
import Skeleton from "../../components/ui-components/Skeleton";

// STAGE_LABELS maps the `stage` values emitted by the SSE status frames
// (assistant_stream_handlers.go) to the progress line shown while the answer is
// being generated. Absent a stage, a calm default is shown.
const STAGE_LABELS = {
  understanding: "Understanding your question…",
  retrieving: "Retrieving data…",
  checking: "Checking figures…",
  writing: "Writing the answer…",
};

// GeneratingIndicator is the "thinking" state shown in a thread slot from the
// moment the drawer opens until the answer arrives: a staged label with three
// staggered dots, above a skeleton that previews the shape of the answer card.
// Motion is disabled under prefers-reduced-motion.
function GeneratingIndicator({ stage }) {
  const label = STAGE_LABELS[stage] || "Generating your answer…";
  return (
    <div className="flex flex-col gap-3" aria-live="polite">
      <div className="flex items-center gap-2 text-sm text-text-2">
        <span className="flex items-end gap-1" aria-hidden="true">
          {[0, 150, 300].map((delay) => (
            <span
              key={delay}
              className="w-[6px] h-[6px] rounded-full bg-primary animate-bounce motion-reduce:animate-none"
              style={{ animationDelay: `${delay}ms` }}
            />
          ))}
        </span>
        <span>{label}</span>
      </div>
      <div className="flex flex-col gap-2 rounded-xl border border-card-bd bg-card p-4" aria-hidden="true">
        <Skeleton className="motion-reduce:animate-none" height={16} width="66%" />
        <Skeleton className="motion-reduce:animate-none" height={12} width="45%" />
        <Skeleton className="mt-1 motion-reduce:animate-none" height={72} width="100%" style={{ borderRadius: 10 }} />
      </div>
    </div>
  );
}

// ThreadItem renders one turn: the question as a quiet prompt label, then either
// the generating state, an error, or the resolved answer card.
function ThreadItem({ item, onNavigate, onChip, onFollowup }) {
  const { question, response, error, stage } = item;
  const pending = !response && !error;
  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex items-start gap-2">
        <FontAwesomeIcon
          icon={icon({ name: "sparkles", family: "sharp", style: "solid" })}
          className="text-primary text-[13px] mt-[3px] shrink-0"
        />
        <p className="text-[15px] font-semibold leading-snug text-text">{question}</p>
      </div>
      {error && <div className="text-sm text-danger">{error}</div>}
      {pending && <GeneratingIndicator stage={stage} />}
      <AssistantAnswer response={response} onNavigate={onNavigate} onChip={onChip} onFollowup={onFollowup} />
    </div>
  );
}

export default function AnalysisPanel({ controller }) {
  const { open, busy, thread, ask, close, onNavigate, conversationId } = controller;
  const [followup, setFollowup] = useState("");
  const navigate = useNavigate();
  const asideRef = useRef(null);
  const previousFocusRef = useRef(null);
  const wasOpenRef = useRef(false);

  // Focus management: move focus into the panel when it opens (recording
  // whatever had focus beforehand), and return focus to that element when the
  // panel closes. No focus-trap library — deliberately just focus-on-open +
  // return-on-close, per the panel's dialog role.
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      previousFocusRef.current = document.activeElement;
      asideRef.current?.focus();
    } else if (!open && wasOpenRef.current) {
      // Only restore focus to the opener if focus would otherwise be lost
      // (it's on the closing panel or nowhere) — never yank it away from an
      // element the user just clicked outside the drawer.
      const active = document.activeElement;
      const focusLost = !active || active === document.body || asideRef.current?.contains(active);
      if (focusLost) previousFocusRef.current?.focus?.();
      previousFocusRef.current = null;
    }
    wasOpenRef.current = open;
  }, [open]);

  // Close when the user clicks anywhere outside the drawer. There is no
  // backdrop — the page stays visible behind the panel (matching the report
  // editor's drawers) — so this is a document-level outside-click listener,
  // active only while the panel is open.
  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e) => {
      if (asideRef.current && !asideRef.current.contains(e.target)) {
        close();
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open, close]);

  const submit = (e) => {
    e.preventDefault();
    if (busy || !followup.trim()) return; // ignore while a request is in flight
    ask(followup);
    setFollowup("");
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") close();
  };

  const openFullAnalysis = () => navigate(`/analysis/${conversationId}`);

  // The panel is always mounted so it can slide in/out (matching the report
  // editor's drawer, 300ms ease). When closed it is translated off-screen,
  // inert (pointer-events-none), and removed from the a11y tree (aria-hidden,
  // no dialog role) — so closed-state assertions still see no dialog.
  const dialogProps = open
    ? { role: "dialog", "aria-modal": "true", "aria-label": "Analysis" }
    : { "aria-hidden": "true" };

  return (
    <aside
      ref={asideRef}
      tabIndex={-1}
      {...dialogProps}
      onKeyDown={onKeyDown}
      className={`fixed top-0 right-0 h-[100vh] w-[600px] max-w-[100vw] bg-bg border-l border-border shadow-float z-[9998] flex flex-col outline-none transition-transform duration-300 ease-in-out motion-reduce:transition-none ${
        open ? "translate-x-0" : "translate-x-full pointer-events-none"
      }`}
    >
      <header className="flex items-center justify-between px-5 h-[60px] border-b border-border gap-2 shrink-0">
        <div className="flex items-center gap-2">
          <FontAwesomeIcon
            icon={icon({ name: "sparkles", family: "sharp", style: "solid" })}
            className="text-primary text-sm"
          />
          <span className="font-semibold text-text">Analysis</span>
        </div>
        <div className="flex items-center gap-3">
          {conversationId && (
            <button
              type="button"
              onClick={openFullAnalysis}
              className="text-sm text-primary hover:text-primary-hover transition-colors"
            >
              Open full analysis<span aria-hidden="true"> →</span>
            </button>
          )}
          <button
            type="button"
            onClick={close}
            aria-label="Close analysis"
            className="w-[32px] h-[32px] flex items-center justify-center rounded-lg text-text-2 hover:text-text hover:bg-surface-2 transition-colors"
          >
            <FontAwesomeIcon icon={icon({ name: "xmark", family: "sharp", style: "regular" })} className="text-[15px]" />
          </button>
        </div>
      </header>

      <div className="flex-1 min-h-0 overflow-y-auto px-5 py-5 flex flex-col gap-7">
        {thread.map((item) => (
          <ThreadItem
            key={item.id}
            item={item}
            onNavigate={onNavigate}
            onChip={(c) => ask(c)}
            onFollowup={(t) => ask(t)}
          />
        ))}
      </div>

      <form onSubmit={submit} className="p-4 border-t border-border shrink-0">
        <div className="flex items-center gap-2 h-[44px] pl-4 pr-2 rounded-xl bg-surface-2 border border-transparent transition-colors focus-within:bg-card focus-within:border-[#BFC8DA] focus-within:shadow-[0_0_0_3px_hsl(var(--primary-soft))]">
          <input
            value={followup}
            onChange={(e) => setFollowup(e.target.value)}
            placeholder="Ask a follow-up…"
            disabled={busy}
            aria-label="Ask a follow-up"
            className="flex-1 bg-transparent outline-none text-sm text-text placeholder:text-text-3 disabled:opacity-60"
          />
          <button
            type="submit"
            disabled={busy || !followup.trim()}
            aria-label="Send follow-up"
            className="w-[32px] h-[32px] flex items-center justify-center rounded-lg bg-primary text-white transition-opacity disabled:opacity-30 disabled:cursor-not-allowed hover:enabled:bg-primary-hover"
          >
            <FontAwesomeIcon icon={icon({ name: "arrow-right", family: "sharp", style: "solid" })} className="text-xs" />
          </button>
        </div>
      </form>
    </aside>
  );
}
